import { useState } from "react"
import { useNavigate } from "react-router-dom"

import {
  deleteQuestion,
  setLearnerPublish,
  setQuestionStatus,
  verifyQuestion,
} from "../../../api/question"
import { QuestionStatus, TQuestion, VerificationStatus } from "../../../api/type"
import {
  errorMessageShow,
  successMessage,
} from "../../../utils/Helper"
import {
  getLearnerPublishBlockers,
  isUnverifiedQuestion,
  stripHtml,
} from "../utils/questionMeta"

type VerificationDecision = Exclude<VerificationStatus, "unverified">
type ConfirmType = "delete" | "publish" | "status" | "learner" | "verify" | null

type UseQuestionActionsOptions = {
  onRemove?: (id: string) => void
  onUpdate?: (id: string, patch: Partial<TQuestion>) => void
  onDeleteSuccess?: () => void
}

export const useQuestionActions = (options: UseQuestionActionsOptions = {}) => {
  const navigate = useNavigate()
  const [confirmType, setConfirmType] = useState<ConfirmType>(null)
  const [pendingId, setPendingId] = useState<string | null>(null)
  const [pendingStatus, setPendingStatus] = useState<QuestionStatus | null>(null)
  const [pendingLearnerValue, setPendingLearnerValue] = useState<boolean | null>(
    null
  )
  const [pendingVerification, setPendingVerification] =
    useState<VerificationDecision | null>(null)
  const [isConfirmLoading, setIsConfirmLoading] = useState(false)

  const closeConfirm = () => {
    if (!isConfirmLoading) {
      setConfirmType(null)
      setPendingId(null)
      setPendingStatus(null)
      setPendingLearnerValue(null)
      setPendingVerification(null)
    }
  }

  const handleEdit = (question: TQuestion) => {
    if (!question.id) return
    navigate(`/questions/edit-question/${question.id}`)
  }

  const handleDelete = async (id: string) => {
    try {
      await deleteQuestion(id)
      options.onRemove?.(id)
      successMessage("Question deleted successfully.")
      options.onDeleteSuccess?.()
    } catch {
      errorMessageShow("Unable to delete the question. Please try again.")
      throw new Error("delete failed")
    }
  }

  const handlePublish = async (id: string) => {
    try {
      await setQuestionStatus(id, "published")
      options.onUpdate?.(id, { question_status: "published" })
      successMessage("Question published successfully.")
    } catch {
      errorMessageShow("Unable to publish the question. Please try again.")
      throw new Error("publish failed")
    }
  }

  const handleSetStatus = async (id: string, status: QuestionStatus) => {
    try {
      await setQuestionStatus(id, status)
      options.onUpdate?.(id, {
        question_status: status,
        ...(status !== "published" ? { published_to_learner: false } : {}),
      })
      successMessage(
        status === "draft"
          ? "Question moved to draft."
          : status === "archived"
            ? "Question archived successfully."
            : "Question unarchived successfully."
      )
    } catch {
      errorMessageShow("Unable to update status. Please try again.")
      throw new Error("status failed")
    }
  }

  const applyLearnerPublish = async (id: string, value: boolean) => {
    try {
      await setLearnerPublish(id, value)
      options.onUpdate?.(id, { published_to_learner: value })
      successMessage(
        value
          ? "Question published to learners."
          : "Question removed from learners."
      )
    } catch {
      errorMessageShow("Unable to update learner visibility. Please try again.")
      throw new Error("learner publish failed")
    }
  }

  const handleVerify = async (id: string, status: VerificationDecision) => {
    try {
      await verifyQuestion(id, status)
      options.onUpdate?.(id, { verification_status: status })
      successMessage(
        status === "verified" ? "Question verified." : "Question rejected."
      )
    } catch {
      errorMessageShow("Unable to update verification. Please try again.")
      throw new Error("verify failed")
    }
  }

  const openLearnerPublishConfirm = (question: TQuestion, value: boolean) => {
    if (!question.id) return
    if (value) {
      if (isUnverifiedQuestion(question)) {
        errorMessageShow("Verify this question before publishing to learners.")
        return
      }
      const blockers = getLearnerPublishBlockers(question)
      if (blockers.length) {
        errorMessageShow(
          `Cannot publish to learners. Missing: ${blockers.join(", ")}.`
        )
        return
      }
    }
    setPendingId(question.id)
    setPendingLearnerValue(value)
    setConfirmType("learner")
  }

  const openDeleteConfirm = (id: string) => {
    setPendingId(id)
    setConfirmType("delete")
  }

  const openPublishConfirm = (question: TQuestion) => {
    if (!question.id) return

    if (isUnverifiedQuestion(question)) {
      errorMessageShow("Verify this question before publishing.")
      return
    }

    if (!stripHtml(question.question ?? "")) {
      errorMessageShow("Question body is required before publishing.")
      return
    }

    setPendingId(question.id)
    setConfirmType("publish")
    setPendingStatus("published")
  }

  const openArchiveConfirm = (question: TQuestion) => {
    if (!question.id) return
    setPendingId(question.id)
    setPendingStatus("archived")
    setConfirmType("status")
  }

  const openRestoreConfirm = (question: TQuestion, status: QuestionStatus) => {
    if (!question.id) return
    setPendingId(question.id)
    setPendingStatus(status)
    setConfirmType("status")
  }

  const openVerifyConfirm = (
    question: TQuestion,
    status: VerificationDecision
  ) => {
    if (!question.id) return
    setPendingId(question.id)
    setPendingVerification(status)
    setConfirmType("verify")
  }

  const handleConfirm = async () => {
    if (!pendingId || !confirmType) return

    setIsConfirmLoading(true)
    try {
      if (confirmType === "delete") {
        await handleDelete(pendingId)
      } else if (confirmType === "learner") {
        if (pendingLearnerValue === null) return
        await applyLearnerPublish(pendingId, pendingLearnerValue)
      } else if (confirmType === "verify") {
        if (!pendingVerification) return
        await handleVerify(pendingId, pendingVerification)
      } else {
        if (!pendingStatus) return
        if (confirmType === "publish") {
          await handlePublish(pendingId)
        } else {
          await handleSetStatus(pendingId, pendingStatus)
        }
      }
      setConfirmType(null)
      setPendingId(null)
      setPendingStatus(null)
      setPendingLearnerValue(null)
      setPendingVerification(null)
    } finally {
      setIsConfirmLoading(false)
    }
  }

  return {
    confirmType,
    pendingStatus,
    pendingLearnerValue,
    pendingVerification,
    isConfirmLoading,
    closeConfirm,
    handleConfirm,
    handleEdit,
    openLearnerPublishConfirm,
    openDeleteConfirm,
    openPublishConfirm,
    openArchiveConfirm,
    openRestoreConfirm,
    openVerifyConfirm,
  }
}
